import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useMediaQuery } from 'react-responsive';

import { Diary } from '../../../../type/recipe';
import { MAX_WIDTH_639PX } from '../../../../utils/common';
import { QueryKeys } from '../../../../utils/common/react-query/query-keys';
import { CommunityGtm } from '../../../../utils/gtm/community/communityGtm';
import useInfiniteDiariesQuery from '../../../../utils/hook/react-query/recipe/useInfiniteDiariesQuery';
import { useHistoryMethod } from '../../../../utils/hook/useHistoryMethod';
import DiaryShortsModal from '../components/DiaryShortsModal';

import RecipeMainDiaryThumbnail from './RecipeMainDiaryThumbnail';

export type DiaryPaginationData =
  | InfiniteData<{
      result: Diary[];
    }>
  | undefined;

export const getInfinityDiariesQueryParams = (isMobile: boolean) =>
  ({
    q: '',
    pageSize: isMobile ? 18 : 9,
    orderBy: 'createdAt',
  }) as const;

const RecipeMainDiaries = () => {
  const queryClient = useQueryClient();
  const history = useHistoryMethod();
  const isMobile = useMediaQuery({ query: MAX_WIDTH_639PX });

  const INFINITY_DIARIES_QUERY_PARAMS = getInfinityDiariesQueryParams(isMobile);

  const {
    data: diaryData,
    fetchNextPage,
    isFetching,
    hasNextPage,
  } = useInfiniteDiariesQuery(INFINITY_DIARIES_QUERY_PARAMS);

  if (!diaryData) return <></>;

  const diaries = diaryData?.pages.flatMap((page) => page.result);
  const totalCount = diaryData.pages[0].totalCount;

  const handlePushToDiary = (diary: Diary) => {
    const communityGtm = new CommunityGtm();
    communityGtm.click_cook_diary({
      recipe_diary_id: diary.id.toString(),
    });

    if (isMobile) {
      return history.push(`/recipes?shortsDiaryId=${diary.id}`);
    }

    history.push(`/recipes/diary/${diary.id}`);
  };

  const loadNextPage = () => {
    if (isFetching || !hasNextPage) return;

    fetchNextPage();
  };

  /**
   * pagination 데이터를 전체 invalidate하는 경우 네트워크 성능에따라 반응성이 다름
   * -> 때문에 setQueryData 하는 방식으로 개선
   */
  const onSuccessToggleLike = async (diaryId: number) => {
    queryClient.setQueryData<DiaryPaginationData>(
      QueryKeys.searchDiary(INFINITY_DIARIES_QUERY_PARAMS),
      (prev) =>
        produce(prev, (draft) => {
          if (!draft) return;

          draft.pages.forEach((page) => {
            page.result.forEach((diary) => {
              if (diary.id === diaryId) {
                diary.isLiked = !diary.isLiked;
                diary.likeCount += diary.isLiked ? 1 : -1;
              }
            });
          });
        }),
    );
  };

  return (
    <div className="flex flex-col gap-24px px-16px">
      <DiaryShortsModal
        diaries={diaries}
        loadNextPage={loadNextPage}
        onSuccessToggleLike={onSuccessToggleLike}
      />
      <div className="flex items-center gap-4px text-22px font-bold">
        <p>따끈 따끈 집밥일기</p>
        <span className="text-brand-1">{totalCount.toLocaleString()}</span>
      </div>
      <div className="grid grid-cols-3 gap-10px sm:grid-cols-6">
        {diaries.slice(0, isMobile ? 6 : 9).map((diary, index) => (
          <RecipeMainDiaryThumbnail
            key={diary.id}
            diary={diary}
            onClick={() => handlePushToDiary(diary)}
            index={index}
          />
        ))}
      </div>
      <button
        onClick={() => history.push(`/recipes/diary`)}
        className="h-48px rounded-10px border border-gray-300 text-16px font-semibold text-gray-600"
        type="button"
      >
        모두 보기
      </button>
    </div>
  );
};

export default RecipeMainDiaries;
